import React, { createContext, FC, ReactNode, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import useFunctions from '../hooks/useFunctions';
import { CampaignTypesResponse } from '../common/data/campaign-data';

export interface IAppContextProps {
	defaultCoffeeImage: string;
	defaultDesertImage: string;
	setDefaultCoffeeImage: (value: string) => void;
	setDefaultDesertImage: (value: string) => void;
	// categories mapped from backend: { typeCode, typeName, svg }
	categories: { key: string; value: string; code?: string; svg?: string }[];
	setCategories: (v: { key: string; value: string; code?: string; svg?: string }[]) => void;
}
const AppContext = createContext<IAppContextProps>({} as IAppContextProps);

interface IAppContextProviderProps {
	children: ReactNode;
}
export const AppContextProvider: FC<IAppContextProviderProps> = ({ children }) => {
	const appStoredData = localStorage.getItem('mudavim_appData');
	const [defaultCoffeeImage, setDefaultCoffeeImage] = useState<string>(
		appStoredData ? JSON.parse(appStoredData).coffeeImage : '',
	);
	const [defaultDesertImage, setDefaultDesertImage] = useState<string>(
		appStoredData ? JSON.parse(appStoredData).desertImage : '',
	);

	const categoriesStored = localStorage.getItem('mudavim_categories');
	const [categories, setCategories] = useState<
		{ key: string; value: string; code?: string; svg?: string }[]
	>(categoriesStored ? JSON.parse(categoriesStored) : []);
	const { callFunction } = useFunctions();

	useEffect(() => {
		localStorage.setItem(
			'mudavim_appData',
			JSON.stringify({ coffeeImage: defaultCoffeeImage, desertImage: defaultDesertImage }),
		);
	}, [appStoredData, defaultCoffeeImage, defaultDesertImage]);

	useEffect(() => {
		localStorage.setItem('mudavim_categories', JSON.stringify(categories));
	}, [categories]);

	useEffect(() => {
		let mounted = true;
		const fetchCategories = async () => {
			try {
				const response = await callFunction<CampaignTypesResponse>(
					'getCategoryTypes',
					{},
					{ showErrorNotification: false },
				);
				if (mounted && response?.categoryList && Array.isArray(response.categoryList)) {
					const mapped = response.categoryList.map((c) => ({
						key: c.typeCode,
						value: c.typeName,
						svg: c.svg,
					}));
					setCategories(mapped);
				}
			} catch (err) {
				// keep existing categories if fetch fails
			}
		};
		fetchCategories();
		return () => {
			mounted = false;
		};
	}, [callFunction]);

	// persist categories to localStorage when they change
	useEffect(() => {
		localStorage.setItem('mudavim_categories', JSON.stringify(categories));
	}, [categories]);

	const value = useMemo(
		() => ({
			defaultCoffeeImage,
			setDefaultCoffeeImage,
			defaultDesertImage,
			setDefaultDesertImage,
			categories,
			setCategories,
		}),
		[
			defaultCoffeeImage,
			setDefaultCoffeeImage,
			defaultDesertImage,
			setDefaultDesertImage,
			categories,
		],
	);
	return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
AppContextProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export default AppContext;
